import { useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { VehicleFitmentDto } from 'utils/types';

const YEAR_RE = /\b((?:19|20)\d{2})\b/g;

export const maxFitmentYear = (yearRange?: string | null): number => {
  if (!yearRange?.trim()) return 0;
  const years: number[] = [];
  YEAR_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = YEAR_RE.exec(yearRange)) !== null) {
    const year = Number(match[1]);
    if (year >= 1980 && year <= 2035) years.push(year);
  }
  return years.length === 0 ? 0 : Math.max(...years);
};

export const sortFitmentsNewestFirst = (
  fitments?: VehicleFitmentDto[] | null
): VehicleFitmentDto[] => {
  if (!fitments?.length) return [];
  return [...fitments].sort((a, b) => {
    const yearDelta = maxFitmentYear(b.yearRange) - maxFitmentYear(a.yearRange);
    if (yearDelta !== 0) return yearDelta;
    const makeDelta = (a.make ?? '').localeCompare(b.make ?? '');
    if (makeDelta !== 0) return makeDelta;
    return (a.model ?? '').localeCompare(b.model ?? '');
  });
};

const formatFitmentLine = (fitment: VehicleFitmentDto, detailed = false): string => {
  const parts: string[] = [];
  if (fitment.make?.trim()) parts.push(fitment.make.trim());
  if (fitment.model?.trim()) parts.push(fitment.model.trim());
  if (fitment.yearRange?.trim()) parts.push(fitment.yearRange.trim());
  if (detailed && fitment.engine?.trim()) parts.push(fitment.engine.trim());
  if (detailed && fitment.usage?.trim()) parts.push(fitment.usage.trim());
  return parts.join(' ');
};

type ProductFitmentPreviewProps = {
  fitments?: VehicleFitmentDto[] | null;
};

const ProductFitmentPreview = ({ fitments }: ProductFitmentPreviewProps) => {
  const [expanded, setExpanded] = useState(false);
  const sorted = useMemo(() => sortFitmentsNewestFirst(fitments), [fitments]);

  if (sorted.length === 0) return null;

  const newest = sorted[0];
  const remaining = sorted.slice(1);

  return (
    <div
      className="text-body-secondary mt-1"
      style={{
        fontSize: '0.7rem',
        lineHeight: 1.35,
        fontWeight: 400,
        whiteSpace: 'normal'
      }}
    >
      <div>{formatFitmentLine(newest)}</div>
      {remaining.length > 0 && (
        <>
          {expanded && (
            <div style={{ maxHeight: '8rem', overflowY: 'auto' }}>
              {remaining.map((fitment, index) => (
                <div
                  key={
                    fitment.productFitmentId ??
                    `${fitment.make}-${fitment.model}-${index}`
                  }
                >
                  {formatFitmentLine(fitment, true)}
                </div>
              ))}
            </div>
          )}
          <Button
            variant="link"
            size="sm"
            className="p-0 fs-10 text-nowrap"
            onClick={event => {
              event.stopPropagation();
              setExpanded(open => !open);
            }}
          >
            {expanded ? 'View less' : `View more (${remaining.length})`}
          </Button>
        </>
      )}
    </div>
  );
};

export default ProductFitmentPreview;
